import React, { useEffect, useMemo, useState } from "react";

/**
 * VoiceWave — flat equalizer bars driven by the live mic spectrum.
 * Ported from the tfcode voice mode: no container chrome, single flat
 * color, log-spaced bands already computed upstream (fast attack,
 * smooth decay).
 *
 * States:
 *  - "connecting": fast sweep animation while the session warms up
 *    (2-3s between clicking live mode and the agent actually listening)
 *  - "listening": slow traveling ripple so the user sees the session is
 *    live while nothing is being said (tfcode idle breathing)
 *  - "user": the USER's mic spectrum drives the bar heights — bright
 *    accent color + glow so voice detection is unmistakable
 *  - "speaking": the live spectrum drives the bar heights
 *  - "thinking": still ripple, dimmed
 */
const VoiceWave = ({ state, bands = [], color, accent, height = 24, bars }) => {
  const [tick, setTick] = useState(0);
  // Mobile: fewer, slightly wider bars so the wave fits next to the mic
  // controls in a <420px input row without crowding them out.
  const barCount = bars || (typeof window !== "undefined" && window.innerWidth < 420 ? 12 : 24);

  // Idle breathing while listening (nothing being said); faster pulse
  // while connecting so the warm-up phase reads as "almost there"
  useEffect(() => {
    if (state !== "listening" && state !== "thinking" && state !== "connecting")
      return undefined;
    const interval = state === "connecting" ? 70 : 140;
    const timer = window.setInterval(() => setTick((t) => t + 1), interval);
    return () => window.clearInterval(timer);
  }, [state]);

  const heights = useMemo(() => {
    const live = state === "speaking" || state === "user";
    const idle = state === "listening" || state === "thinking";
    if (state === "connecting") {
      // Energizing sweep: a bright pulse travels across the bars,
      // clearly "not live yet" but visibly progressing
      const t = Date.now() / 220;
      return new Array(barCount).fill(0).map((_, i) => {
        const wave = (Math.sin(t - i * 0.45) + 1) / 2;
        return 2 + Math.round(wave * 7);
      });
    }
    if (idle) {
      const t = Date.now() / 500;
      return (bands.length ? bands : new Array(barCount).fill(0)).map(
        (_, i) => 2 + Math.round(((Math.sin(t * 0.6 + i * 0.55) + 1) / 2) * 3)
      );
    }
    return bands.slice(0, barCount).map((v) => 3 + Math.round(v * 21));
  }, [state, bands, tick, barCount]);

  const barColor =
    state === "user"
      ? accent || color || "currentColor"
      : state === "connecting"
        ? "#94a3b8"
        : state === "speaking"
          ? "#22c55e"
          : state === "thinking"
            ? "#f59e0b"
            : color || "currentColor";

  // Glow halo when the user's voice is actively driving the wave —
  // unmistakable "I am being heard" feedback on top of the bar motion.
  const glow =
    state === "user"
      ? `0 0 8px ${accent || color || "currentColor"}66, 0 0 14px ${
          accent || color || "currentColor"
        }33`
      : "none";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 3,
        height,
        opacity: state === "thinking" ? 0.7 : 1,
      }}
      aria-hidden
    >
      {heights.map((h, i) => (
        <span
          key={i}
          style={{
            width: barCount <= 12 ? 4 : 3,
            borderRadius: 2,
            height: `${h}px`,
            background: barColor,
            transition: "height 80ms ease-out",
            transitionDelay: `${i * 4}ms`,
            willChange: "height",
            boxShadow: glow,
          }}
        />
      ))}
    </div>
  );
};

export default VoiceWave;
